import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectColorTheme,
    selectData,
    selectEnabled,
    selectJobId,
    selectOrderedItemIndexes,
    selectPageSize,
    selectPDFLegendScale,
    selectPDFLegendSize,
    selectSecondaryStats,
    selectVisibleStatItems,
    setActiveMenu,
    setData,
} from "../PDFSlice"
import { AreaUnit, DistanceUnit } from "@nova/constants"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { useTranslation } from "@nova/use-translation"
import { InsetComponent } from "./Inset"
import {
    selectActiveFirePerimeterPolygons,
    selectVisibleFirePerimeters,
} from "features/entity-caching/fire-perimeters/selectors"
import { ExpandableQRCode } from "features/data-management/workspace/TODO-put-in-package/ExpandableQRCode"
import { PropsWithChildren, useEffect, useMemo } from "react"
import classNames from "classnames"
import { ScaleBar } from "./PDFScaleBar"
import { LngLat } from "maplibre-gl"
import { config } from "@nova/configuration/browser"
import { useLegendItems } from "./LegendItems/useLegendItems"
import { useDebounceDispatch } from "tools/store"
import * as Icons from "@nova/component-icons"
import { DEFAULT_WIDTH } from "../PDFSnapshotContainer"
import { featureNameKey } from "../PDFPanel/buttons/PDFFeatures"
import { resetBearingAction } from "features/map/control/slice"
import { useViewport } from "features/map/GlobalViewState"
import { calculateArea } from "@nova/calculate-geospatial"
import { formatArea } from "@nova/format"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

const Feature = ({ children, id }: PropsWithChildren & { id: string }) => {
    const dispatch = useAppDispatch()
    return (
        <div
            className="pointer-events-auto cursor-pointer hover:border-black border [&:not(:hover)]:border-[#00000000] p-0.5 rounded-md"
            onClick={() => dispatch(setActiveMenu(id))}
        >
            {children}
        </div>
    )
}

type PDFAnnotationsType = {
    offsetX: number
    offsetY: number
    width: number
    height: number
    name: string
}

export const BORDER_THICKNESS = 12
export const BORDER_TICK_LENGTH = 0

export const PDFBottomLegend = ({ offsetX, offsetY, width, height, name }: PDFAnnotationsType) => {
    const pdfLegendSize = useAppSelector(selectPDFLegendSize())
    const pdfLegendScale = useAppSelector(selectPDFLegendScale())
    const innerHeight = height - (pdfLegendSize * width) / DEFAULT_WIDTH
    const { t } = useTranslation()
    const { debounceDispatch } = useDebounceDispatch(100)
    const enabled = useAppSelector(selectEnabled())
    const text1 = useAppSelector(selectData("text1"))
    const projection = useAppSelector(selectData("projection"))
    const datum = useAppSelector(selectData("datum"))
    const createdAt = useAppSelector(selectData("createdAt"))
    const createdBy = useAppSelector(selectData("createdBy"))
    const operator = useAppSelector(selectData("operator"))
    const title = useAppSelector(selectData("title"))
    const subtitle = useAppSelector(selectData("subtitle"))
    const date = useAppSelector(selectData("date"))
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const fire = useAppSelector(selectFireById(fireId))!
    const jobId = useAppSelector(selectJobId())
    const isDark = useAppSelector(selectColorTheme()) === "dark"
    const dispatch = useAppDispatch()

    const visibleStats = useAppSelector(selectVisibleStatItems())
    const visibleSecondaryStats = useAppSelector(selectSecondaryStats())

    const leftLogo = useAppSelector(selectData("leftLogo"))
    const rightLogo = useAppSelector(selectData("rightLogo"))
    const ratio = useAppSelector(selectData("ratio"))
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }
    const view = useViewport()

    const left = new LngLat(...(view.unproject([offsetX, offsetY]) as [number, number]))
    const right = new LngLat(...(view.unproject([offsetX, offsetY + height]) as [number, number]))
    const dist = left.distanceTo(right)
    const size = useAppSelector(selectPageSize())
    const calculatedRatio = dist / (parseFloat(size.split('"')[0]) * 0.0254)

    const { organizationSettingArea } = config.use("organizationSettingArea") as {
        organizationSettingArea: AreaUnit
    }
    const perimeters = useAppSelector(selectVisibleFirePerimeters)
    const perimeterArea = useMemo(() => {
        return formatArea({
            value: perimeters.reduce((area, perimeter) => {
                return (
                    area +
                    perimeter.polygons.reduce(
                        (area, polygon) => area + calculateArea(polygon.coordinates),
                        0
                    )
                )
            }, 0),
            unit: organizationSettingArea,
        })
    }, [organizationSettingArea, perimeters])

    const { enabledLegendItems } = useLegendItems({
        offsetX,
        offsetY,
        width,
        height,
    })
    const orderedItemIndexes = useAppSelector(selectOrderedItemIndexes())
    const orderedItems = useMemo(
        () =>
            enabledLegendItems.sort((a, b) => {
                const indexA = orderedItemIndexes[a.id]
                const indexB = orderedItemIndexes[b.id]
                return indexA - indexB
            }),
        [orderedItemIndexes, enabledLegendItems]
    )

    const displayedRatio =
        Math.abs(parseInt(ratio) / calculatedRatio - 1) < 0.01
            ? parseInt(ratio).toFixed(0)
            : calculatedRatio.toFixed(0)

    useEffect(() => {
        debounceDispatch(setData({ type: "displayedRatio", data: displayedRatio.toString() }))
    }, [displayedRatio, debounceDispatch])

    const scale = (width / DEFAULT_WIDTH) * pdfLegendScale
    const availableHeight = (pdfLegendSize * width) / DEFAULT_WIDTH / scale - 30 / scale
    return (
        <div
            className="absolute left-0 bottom-0 bg-white dark:text-gray-0 dark:bg-gray-900 border-left-[#B0B0B0] border-t border-t-gray-600 flex flex-row items-center"
            style={{ height: (pdfLegendSize * width) / DEFAULT_WIDTH, width: DEFAULT_WIDTH * 3 }}
        >
            <div
                className="flex flex-col justify-between border-transparent"
                style={{
                    width: width / scale,
                    transformOrigin: "center left",
                    transform: `scale(${scale})`,
                    height: (pdfLegendSize * width) / DEFAULT_WIDTH / scale,
                    maxHeight: (pdfLegendSize * width) / DEFAULT_WIDTH / scale,
                    borderWidth: 12 / scale,
                }}
            >
                <div className="flex flex-1 justify-between p-0.5 h-full">
                    <div className="flex min-w-0">
                        <div className="flex flex-col text-center min-w-[150px]">
                            {enabled.title && (
                                <Feature id={"title"}>
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-lg break-words">
                                            {t(title || fire.name)}
                                        </span>
                                        <div className="font-normal text-xs">{t(subtitle)}</div>
                                        <div className="font-normal text-xs">{t(date)}</div>
                                        {perimeters.length > 0 && (
                                            <div className="font-normal text-[10px]">
                                                {perimeterArea}
                                            </div>
                                        )}
                                    </div>
                                </Feature>
                            )}
                            {(enabled.scalebar || enabled.ratio) && (
                                <div>
                                    {enabled.scalebar && (
                                        <ScaleBar
                                            view={view}
                                            u={organizationSettingDistance}
                                            height={8}
                                            color={isDark ? "#FFF" : "#000"}
                                        />
                                    )}
                                    {enabled.ratio && (
                                        <Feature id="ratio">
                                            <div className="text-center text-xxs">
                                                1:
                                                {t(displayedRatio)}
                                            </div>
                                        </Feature>
                                    )}
                                </div>
                            )}
                        </div>
                        {enabled.features && (
                            <Feature id="features">
                                <div
                                    className="flex flex-col flex-wrap gap-y-0.5 gap-x-2"
                                    style={{ maxHeight: availableHeight }}
                                >
                                    {orderedItems.map((item) => {
                                        const localRename = JSON.parse(
                                            localStorage.getItem(featureNameKey) || "{}"
                                        )[item.id] as string
                                        const isStatVisible = visibleStats[item.id]
                                        const isSecondaryStatVisible =
                                            visibleSecondaryStats[item.id]
                                        return (
                                            <div key={item.id} className="flex flex-row w-fit">
                                                <div className="mr-1.5 flex w-[14px] h-[14px] my-auto items-center">
                                                    {item.icon}
                                                </div>
                                                <div className="flex flex-col overflow-hidden">
                                                    <div className="text-xxs leading-4 overflow-hidden whitespace-nowrap text-ellipsis">
                                                        {t(localRename || item.title)}
                                                    </div>
                                                    {item.stat &&
                                                        (isStatVisible ||
                                                            isSecondaryStatVisible) && (
                                                            <div className="text-xxs leading-4 overflow-hidden whitespace-nowrap text-ellipsis w-full">
                                                                {isStatVisible && item.stat.stat}
                                                                {isStatVisible &&
                                                                    isSecondaryStatVisible &&
                                                                    " | "}
                                                                {isSecondaryStatVisible &&
                                                                    item.secondaryStat?.stat}
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </Feature>
                        )}
                    </div>
                    <div className="flex gap-4">
                        {enabled.inset && (
                            <InsetComponent
                                offsetX={offsetX}
                                offsetY={offsetY}
                                width={width}
                                height={innerHeight}
                                polygons={polygons}
                            />
                        )}

                        <div className="flex gap-2 justify-center items-center">
                            {enabled.text1 && (
                                <Feature id="text1">
                                    <div className="overflow-hidden break-words text-xxs whitespace-pre-wrap leading-[12px] w-[150px] dark:text-gray-200">
                                        {text1}
                                    </div>
                                </Feature>
                            )}
                            {enabled.text2 && (
                                <Feature id="text2">
                                    <div className="overflow-hidden break-words text-xxs whitespace-nowrap leading-[12px] dark:text-gray-200">
                                        {enabled.projection && (
                                            <div>{`Projection: ${projection}`}</div>
                                        )}
                                        {enabled.datum && <div>{`Datum: ${datum}`}</div>}
                                        {enabled.createdAt && (
                                            <div>{`Date Created: ${createdAt}`}</div>
                                        )}
                                        {enabled.createdBy && (
                                            <div>{`Map Produced by: ${createdBy}`}</div>
                                        )}
                                        {enabled.operator && (
                                            <div>{`Camera Operator / Type: ${operator}`}</div>
                                        )}
                                    </div>
                                </Feature>
                            )}
                        </div>

                        {enabled.logos && (leftLogo || rightLogo) && (
                            <div className="max-w-[100px] flex flex-col gap-2 h-full">
                                {leftLogo && (
                                    <div className="flex flex-1 min-h-0">
                                        <img
                                            src={leftLogo}
                                            alt="left logo"
                                            className="m-auto max-w-full max-h-full"
                                        />
                                    </div>
                                )}
                                {rightLogo && (
                                    <div className="flex flex-1 min-h-0">
                                        <img
                                            src={rightLogo}
                                            alt="right logo"
                                            className="m-auto max-w-full max-h-full"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex flex-col items-center justify-center gap-2 h-full">
                            {enabled.compass && (
                                <div className="flex flex-1">
                                    <div
                                        className={classNames(
                                            "w-7 h-7 flex m-auto items-center justify-center rounded-full cursor-pointer pointer-events-auto border",
                                            {
                                                "bg-black-600 hover:bg-gray-700 border-white-300 hover:border-white-400":
                                                    isDark,
                                                "bg-white-500 hover:bg-gray-0 border-black-300 hover:border-black-400":
                                                    !isDark,
                                            }
                                        )}
                                        onClick={() => dispatch(resetBearingAction())}
                                    >
                                        <Icons.SmallIcon
                                            Icon={
                                                !isDark
                                                    ? Icons.CompassNeedleDark
                                                    : Icons.CompassNeedleLight
                                            }
                                            style={{ transform: `rotate(${-view.bearing}deg)` }}
                                            className={classNames({
                                                "text-gray-500": isDark,
                                                "text-gray-0": !isDark,
                                            })}
                                        />
                                    </div>
                                </div>
                            )}
                            {enabled.qrcode && (
                                <div className="flex flex-1 ">
                                    <div className="w-max m-auto">
                                        <ExpandableQRCode
                                            title={t("Map PDF")}
                                            size={40}
                                            value={`${window.location.origin}/api/organization/${workspaceId}/job/${jobId}/map.pdf?n=${name}`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="self-end text-xxs absolute" style={{ bottom: -8 / scale }}>
                    {t("Map created with mapnova.com")}
                </div>
            </div>
        </div>
    )
}
